/**
 * Graphing in Slope-Intercept Form generator (Big Ideas Math 3.5).
 *
 * A line y = mx + b is fully described by its slope m and y-intercept b. This
 * topic exercises reading and matching that form against a graph, as
 * deterministic multiple choice (selection, not construction - consistent with
 * the other graph topics in V1):
 *
 *   S1: equation -> graph   show y = mx + b, pick the matching line
 *   S2: graph -> slope      show a line, pick its slope
 *   S3: graph -> intercept  show a line, pick its y-intercept
 *
 * Every choice graph is a CoordinateModel built from exact rationals and
 * rendered by the shared coordinate renderer. Distractors encode the classic
 * confusions: wrong slope sign, swapped slope/intercept, shifted intercept.
 * Grading is exact choice matching (S1) or an exact number (S2/S3).
 */

import { rat, type Rational } from "../engine/rational";
import { Rng } from "../engine/rng";
import type { CoordinateModel } from "../graph/plot-model";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Choice,
  Question,
  QuestionSignature,
} from "./types";
import { getTopic } from "../curriculum/curriculum";

const TOPIC = "graphing-slope-intercept" as const;
const VERSION = "1.0.0";

type Family = "S1" | "S2" | "S3";

interface Cfg {
  readonly families: readonly Family[];
  readonly slopeMax: number;
  readonly interceptMax: number;
  readonly pNeg: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["S2", "S3"], slopeMax: 3, interceptMax: 5, pNeg: 0.2 },
  standard: { families: ["S1", "S2", "S3"], slopeMax: 4, interceptMax: 6, pNeg: 0.4 },
  hard: { families: ["S1", "S2", "S3"], slopeMax: 5, interceptMax: 7, pNeg: 0.5 },
  challenge: { families: ["S1", "S2"], slopeMax: 6, interceptMax: 8, pNeg: 0.5 },
};

/** Render y = mx + b as a line across a fixed window with the y-intercept marked. */
function lineGraph(m: Rational, b: Rational): CoordinateModel {
  const xMin = -8;
  const xMax = 8;
  const yAt = (x: number) => m.mul(rat(x)).add(b).toNumber();
  const ys = [yAt(xMin), yAt(xMax), b.toNumber()];
  const yMin = Math.floor(Math.min(...ys)) - 1;
  const yMax = Math.ceil(Math.max(...ys)) + 1;
  return {
    kind: "coordinate",
    xMin,
    xMax,
    yMin,
    yMax,
    points: [{ x: rat(0), y: b, closed: true }], // the y-intercept
    segments: [
      {
        from: { x: rat(xMin), y: m.mul(rat(xMin)).add(b) },
        to: { x: rat(xMax), y: m.mul(rat(xMax)).add(b) },
      },
    ],
  };
}

/** LaTeX for y = mx + b with sensible 1/-1 and sign handling. */
function equationLatex(m: Rational, b: Rational): string {
  const mPart = m.equals(rat(1)) ? "x" : m.equals(rat(-1)) ? "-x" : `${m.toLatex()}x`;
  if (b.isZero()) return `y = ${mPart}`;
  return `y = ${mPart} ${b.isNegative() ? `- ${b.neg().toLatex()}` : `+ ${b.toLatex()}`}`;
}

export class GraphingSlopeInterceptGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = ["easy", "standard", "hard", "challenge"];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const cfg = DIFF[ctx.difficulty];
      const family = rng.pick(cfg.families);
      // Nonzero slope keeps the line non-horizontal (slope is meaningful).
      const m = rat(rng.int(1, cfg.slopeMax) * rng.sign(cfg.pNeg));
      const b = rat(rng.int(1, cfg.interceptMax) * rng.sign(cfg.pNeg));

      if (family === "S1") {
        // equation -> pick the graph. Distractor lines defined by their (m,b):
        // negated slope, swapped m<->b, shifted intercept.
        const swapM = b.isZero() ? rat(1) : b;
        const distractorLines: Array<[Rational, Rational]> = [
          [m.neg(), b],
          [swapM, m],
          [m, b.add(rat(b.isNegative() ? 2 : -2))],
        ];
        // Each distractor line must differ from the correct line AND from the
        // others; otherwise two choices would render identically. Regenerate
        // (next attempt) rather than emit an ambiguous item.
        const lineKey = (lm: Rational, lb: Rational) => `${lm.key()}|${lb.key()}`;
        const keys = new Set<string>([lineKey(m, b)]);
        let distinct = true;
        for (const [lm, lb] of distractorLines) {
          const k = lineKey(lm, lb);
          if (keys.has(k)) { distinct = false; break; }
          keys.add(k);
        }
        if (!distinct) continue;

        const correct: Choice = { id: "correct", graph: lineGraph(m, b) };
        const choices = rng.shuffle([
          correct,
          { id: "d0", graph: lineGraph(distractorLines[0]![0], distractorLines[0]![1]) },
          { id: "d1", graph: lineGraph(distractorLines[1]![0], distractorLines[1]![1]) },
          { id: "d2", graph: lineGraph(distractorLines[2]![0], distractorLines[2]![1]) },
        ]);
        return this.mc(ctx, topic, family, {
          text: `Which graph shows ${equationLatex(m, b)}?`,
          latex: `\\text{Which graph shows } ${equationLatex(m, b)}\\text{?}`,
        }, choices, m, b);
      }

      if (family === "S2") {
        // graph -> slope (number answer).
        const q: Question = {
          ...this.base(ctx, topic, family, {
            text: "What is the slope of the line shown?",
            latex: "\\text{What is the slope of the line shown?}",
          }),
          answerType: "number",
          answer: { kind: "number", value: m },
          steps: [
            {
              description:
                "In y = mx + b, the slope is the coefficient m. Read rise over run, or read m from the equation form.",
              latex: `\\text{slope} = ${m.toLatex()}`,
            },
          ],
          hints: [
            { level: 1, text: "Slope is rise over run - how much y changes per step in x." },
            { level: 2, text: "In y = mx + b, the slope is m." },
          ],
          subskills: topic.subskills,
        };
        return { ...q, graph: lineGraph(m, b) };
      }

      // S3: graph -> y-intercept (number answer). Read where the line crosses
      // the y-axis (the value b).
      const q: Question = {
        ...this.base(ctx, topic, family, {
          text: "What is the y-intercept of the line shown?",
          latex: "\\text{What is the y-intercept of the line shown?}",
        }),
        answerType: "number",
        answer: { kind: "number", value: b },
        steps: [
          {
            description:
              "The y-intercept is where the line crosses the y-axis (x = 0). In y = mx + b, it is b.",
            latex: `\\text{y-intercept} = ${b.toLatex()}`,
          },
        ],
        hints: [
          { level: 1, text: "The y-intercept is the y-value where the line crosses the y-axis." },
          { level: 2, text: "In y = mx + b, the y-intercept is b." },
        ],
        subskills: topic.subskills,
      };
      return { ...q, graph: lineGraph(m, b) };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  /** Shared base fields for a question. */
  private base(
    ctx: GenerateContext,
    topic: ReturnType<typeof getTopic>,
    family: Family,
    prompt: Question["prompt"],
  ): Omit<Question, "answerType" | "answer" | "steps" | "hints" | "subskills"> {
    return {
      id: `${TOPIC}-${ctx.seed}-${family}`,
      generator: TOPIC,
      generatorVersion: VERSION,
      seed: ctx.seed,
      unit: topic.unit,
      topic: TOPIC,
      family,
      difficulty: ctx.difficulty,
      prompt,
    };
  }

  /** Assemble a choice (S1) question. */
  private mc(
    ctx: GenerateContext,
    topic: ReturnType<typeof getTopic>,
    family: Family,
    prompt: Question["prompt"],
    choices: Choice[],
    m: Rational,
    b: Rational,
  ): Question {
    return {
      ...this.base(ctx, topic, family, prompt),
      answerType: "choice",
      answer: { kind: "choice", value: "correct" },
      choices,
      steps: [
        {
          description:
            "Plot the y-intercept b on the y-axis, then use the slope m (rise over run) to find a second point and draw the line.",
          latex: equationLatex(m, b),
        },
      ],
      hints: [
        { level: 1, text: "Start at the y-intercept b on the y-axis." },
        { level: 2, text: "From there, use the slope m as rise over run to plot another point." },
      ],
      subskills: topic.subskills,
    };
  }

  signature(q: Question): QuestionSignature {
    const structure =
      q.answer.kind === "number" ? q.answer.value.key() : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const graphingSlopeIntercept = new GraphingSlopeInterceptGenerator();
